/* import __COLOCATED_TEMPLATE__ from './block-group-composer.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { type Block } from '@intercom/interblocks.ts';
import { type BlockGroup } from '../shared/suggestion-editor';
import { BlocksDocument } from '@intercom/embercom-prosemirror-composer';
import { EntityType } from 'embercom/models/data/entity-types';
import type IntlService from 'ember-intl/services/intl';
import ArticlesComposerConfig from 'embercom/lib/articles/composer-config';
import type Model from '@ember-data/model';
import { type LocalizedKnowledgeContent } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import type ArticleContent from 'embercom/models/articles/article-content';
import {
  copyHeadingLinkToClipboard,
  getSelectionForAI,
} from 'embercom/helpers/articles/editor-helper';
import type ClipboardService from 'embercom/services/clipboard-service';
import ENV from 'embercom/config/environment';
import { ContentSnippetComposerConfig } from 'embercom/components/content-service/composer/content-snippet-editor';

interface Signature {
  Element: HTMLSpanElement;
  Args: {
    updateBlocksForBlockGroup: (blockGroup: BlockGroup, blocks: Block[]) => void;
    blockGroup: BlockGroup;
    contentEntityType: EntityType;
    activeContent: LocalizedKnowledgeContent & Model;
  };
}

export default class BlockGroupComposer extends Component<Signature> {
  @service declare appService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare clipboardService: ClipboardService;

  get composerConfig() {
    if (this.args.contentEntityType === EntityType.ContentSnippet) {
      return new ContentSnippetComposerConfig({
        eventService: this.intercomEventService,
        intl: this.intl,
        placeholder: this.intl.t('ai-content-library.content-snippet.editor.placeholder'),
      });
    }
    return this.articleComposerConfig;
  }

  get articleContent() {
    return this.args.activeContent as unknown as ArticleContent;
  }

  get articleComposerConfig() {
    let eventService = this.intercomEventService;
    let analytics = {
      trackAnalyticsEvent: (eventName: any, attrs: any) => {
        eventService.trackAnalyticsEvent({
          action: eventName,
          object: 'article',
          place: 'composer',
          ...attrs,
        });
      },
    };

    let anchorLinkOnClick = (heading: any) => {
      copyHeadingLinkToClipboard(
        heading,
        this.articleContent,
        this.notificationsService,
        this.intl,
        this.clipboardService,
        this.intercomEventService,
        this.articleContent.locale ?? '',
      );
    };

    let config = new ArticlesComposerConfig(
      this.appService.app,
      this.articleContent.textDirection,
      analytics,
      anchorLinkOnClick,
      true,
      'media-inserter',
    );

    if (this.appService.app.canUseArticlesAi) {
      config.formatters = {
        placement: 'right',
        config: [{ icon: 'ai', key: 'ai-expand' }].map(({ icon, key }) => ({
          icon,
          name: key,
          transform: async (selectedBlocks: any) => {
            this.notificationsService.notify(
              this.intl.t('articles.editor.ai-running'),
              ENV.APP._3000MS,
            );
            let blocks = await getSelectionForAI(
              selectedBlocks,
              this.appService.app,
              this.articleContent.id,
              this.notificationsService,
            );
            return blocks;
          },
          description: this.intl.t(`articles.editor.${key}`),
        })),
      };
    }

    return config;
  }

  get blocksDocument() {
    return new BlocksDocument(this.args.blockGroup.blocks);
  }

  @action
  composerChanged(newBlocksDocument: BlocksDocument) {
    this.args.updateBlocksForBlockGroup(this.args.blockGroup, newBlocksDocument.blocks);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ContentReview::BlockGroupComposer': typeof BlockGroupComposer;
    'knowledge-hub/content-editor/content-review/block-group-composer': typeof BlockGroupComposer;
  }
}
